import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Popover,
  PopoverPosition,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Check as CheckIcon } from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useTimeout } from '../../../common/timeout.hook';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useFindUsers } from '../../../user/User.hooks';
import { User, UserPermissionFilter } from '../../../user/User.interface';
import { useFindGroups } from '../../groups/Group.hooks';
import { Group } from '../../groups/Group.interface';
import GroupSelector from '../../workflow/components/GroupSelector';
import { checkGroupIds } from '../../workflow/plugins/transition-conditions/CheckSubTasksPlugin';
import { compareTaskStatus } from '../task.helper';
import { useUpdateTask } from '../Task.hooks';
import { SubTaskUser, Task } from '../Task.interface';

const StyledUl = styled.ul`
  margin: 0;
  font-size: 12px;
  padding-left: 16px;
  li:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  .MuiTableHead-root .MuiTableCell-root {
    padding-top: 8px;
    padding-bottom: 8px;
    background-color: ${props => props.theme.palette.grey[100]};
  }
  .MuiTableBody-root .MuiTableCell-root {
    padding-top: 0px;
    padding-bottom: 0px;
  }
  .row-divider {
    background-color: ${props => props.theme.palette.grey[100]};
  }
  .groups {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
`;

const StyledForm = styled.form`
  width: 300px;
  padding: 8px;
  .MuiAlert-root {
    padding: 0;
  }
  .MuiAlert-icon {
    /* hide icon */
    display: none;
  }
  .MuiAlert-message {
    padding: 8px;
  }
  .menu-selection-actions {
    display: flex;
    justify-content: end;
    gap: 8px;
  }
`;

export interface SubTaskUserItem {
  id?: number;
  version?: number;
  user: User;
  group: Group | undefined;
  statePath?: string | null;
  isOptional?: boolean;
}

export interface TaskAssignDialogProps {
  taskId: number;
  groupIds?: number[];
  userFilter?: UserPermissionFilter;
  open: boolean;
  title?: string;
  message?: string;
  multiple?: boolean;
  parentTask?: Task;
  lockedUsers?: SubTaskUserItem[];
  initialUsers?: SubTaskUserItem[];
  isLegacyAssignment?: boolean;
  handleAssign?: (subTaskUsers: SubTaskUser[]) => void;
  onCancel?: () => void;
  onAssign?: (task: Task) => void;
}

const TaskAssignDialog: React.FC<TaskAssignDialogProps> = props => {
  const {
    taskId,
    groupIds,
    userFilter,
    open,
    title = 'Assign Task',
    message = 'Select a user to assign the task.',
    multiple,
    parentTask,
    lockedUsers,
    initialUsers,
    isLegacyAssignment,
    handleAssign,
    onCancel,
    onAssign
  } = props;
  if (multiple && !handleAssign) {
    throw new Error(
      'TaskAssignDialog multiple select requires a custom `handleAssign` override.'
    );
  }
  const onAssignRef = useRef(onAssign);
  onAssignRef.current = onAssign;
  const initialUsersRef = useRef(initialUsers);
  initialUsersRef.current = initialUsers;
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);
  const [search, setSearch] = useState('');
  const [menuSelection, setMenuSelection] = useState<{
    user: User;
    group?: Group;
    anchorEl: HTMLElement;
    anchorPosition?: PopoverPosition;
    /** Determines if the current User is selected. */
    selected: boolean;
  }>();
  const [selectedGroupId, setSelectedGroupId] = useState<number>();
  const [subTaskUsersState, setSubTaskUsers] = useState<SubTaskUserItem[]>(
    () => initialUsers || []
  );
  const [debounceSearch] = useTimeout();
  const {
    userList,
    isLoading: isFindingUsers,
    error: usersError,
    get
  } = useFindUsers();
  const {
    groupList,
    isLoading: isFindingGroups,
    error: groupsError,
    get: findGroups
  } = useFindGroups();
  // update the task when submitting (this is bypassed if handleAssign is passed in props)
  const { updatedTask, isUpdating, updateError, updateTask } =
    useUpdateTask(taskId);

  const subTaskUsers = useMemo(() => {
    return (lockedUsers || []).concat(subTaskUsersState);
  }, [lockedUsers, subTaskUsersState]);

  const maxColSpan = 4 + +(subTaskUsers.length > 0);
  const isLoading = isFindingUsers || isFindingGroups;
  const error = usersError || groupsError || updateError;

  const filteredUsers = useMemo(() => {
    // remove selected users since they are displayed above the user list
    return (userList?.data || []).filter(user => {
      return !subTaskUsers.some(subTask => subTask.user.id === user.id);
    });
  }, [subTaskUsers, userList]);

  const isValidSelectedUsers = useMemo(() => {
    if (!groupIds || groupIds.length === 0) {
      // skip check since there are no groupIds when adding more approvers
      return true;
    }
    return checkGroupIds({
      groupIds,
      // ignore optional subTasks
      items: subTaskUsers.filter(subTask => !subTask.isOptional),
      inGroup: (subTask, groupId) => {
        return isLegacyAssignment
          ? (subTask.user.groups || []).some(group => group.id === groupId)
          : !!subTask.group && subTask.group.id === groupId;
      }
    });
  }, [groupIds, isLegacyAssignment, subTaskUsers]);

  const isRequiredGroupMet = (groupId: number) => {
    // note that groupId may not be part of the required groups
    // and can have duplicates (multiple users per group)
    const allGroupIds = (groupIds || []).filter(id => id === groupId);
    // check if group has met total number of required groups
    const matchedSubTasks = subTaskUsers.filter(subTask => {
      // a subTaskUser is part of the group if it's not optional
      // and it matches either one of the user groups (old behavior)
      // or the assigned subtask group
      return (
        !subTask.isOptional &&
        (isLegacyAssignment
          ? (subTask.user.groups || []).some(group => group.id === groupId)
          : subTask.group?.id === groupId)
      );
    });
    return matchedSubTasks.length >= allGroupIds.length;
  };

  const toggle = (user: User) => {
    setSubTaskUsers(subTasks => {
      const index = subTasks.findIndex(subTask => subTask.user.id === user.id);
      if (index === -1) {
        const subTask: SubTaskUserItem = { user, group: undefined };
        return multiple ? subTasks.concat(subTask) : [subTask];
      }
      const copy = Array.from(subTasks);
      copy.splice(index, 1);
      return copy;
    });
  };

  const renderRow = (user: User, subTask?: SubTaskUserItem) => {
    const locked = (lockedUsers || []).some(lockedUser => {
      return (
        lockedUser.user.id === user.id &&
        // ignore if no subTask provided
        (!subTask ||
          // make sure subTask ids match if provided
          ((!subTask.id || !lockedUser.id || subTask.id === lockedUser.id) &&
            // make sure versions match if provided
            (!subTask.version ||
              !lockedUser.version ||
              subTask.version === lockedUser.version) &&
            // make sure statePaths match if provided
            (!subTask.statePath ||
              !lockedUser.statePath ||
              compareTaskStatus(subTask.statePath, lockedUser.statePath))))
      );
    });
    const userGroups = user.groups || [];
    // if selected (has subTask), should only display the selected subTask group
    // note that selected subTask can have no group assignment
    const rowGroups = !subTask
      ? userGroups
      : subTask.group
      ? [subTask.group]
      : [];
    return (
      <TableRow
        key={user.id}
        role='checkbox'
        color='primary'
        hover={!locked}
        selected={!!subTask}
        onClick={event => {
          if (locked) {
            return;
          } else if (
            isLegacyAssignment ||
            !user.groups ||
            user.groups.length === 0
          ) {
            // toggle directly if legacy assignment and if user has no groups
            return toggle(user);
          }

          // for convenience, auto select the group that the user is likely to assign
          // based on the required groupIds
          const selectedGroup =
            subTask?.group ||
            user.groups.find(group => {
              // find a group that is part of required groups
              // and check if the requirement is not yet met
              return (
                groupIds?.includes(group.id) && !isRequiredGroupMet(group.id)
              );
            });

          setMenuSelection({
            user,
            selected: !!subTask,
            group: selectedGroup,
            anchorEl: event.currentTarget,
            anchorPosition: { top: event.clientY, left: event.clientX }
          });
        }}
      >
        <TableCell padding='checkbox'>
          <Checkbox
            size='small'
            color='primary'
            disabled={locked}
            checked={!!subTask}
            onClick={event => {
              event.stopPropagation();
              // toggle user directly if selected
              if (subTask) {
                toggle(user);
              } else {
                setMenuSelection({
                  user,
                  selected: false,
                  anchorEl: event.currentTarget
                });
              }
            }}
          />
        </TableCell>
        <TableCell>{user.firstName}</TableCell>
        <TableCell>{user.lastName}</TableCell>
        <WrappedTableCell
          colSpan={!subTask && subTaskUsers.length > 0 ? 2 : undefined}
        >
          <Tooltip
            // hide tooltip if no user groups
            title={
              userGroups.length === 0 ? (
                ''
              ) : (
                <StyledUl>
                  {userGroups.map(group => {
                    const selected = subTask?.group?.id === group.id;
                    return (
                      <li key={group.id}>
                        {selected ? <strong>{group.name}</strong> : group.name}
                        {selected && ' - (selected)'}
                      </li>
                    );
                  })}
                </StyledUl>
              )
            }
          >
            <div className='groups'>
              {/* should selected groups when selected */}
              {rowGroups.length === 0 ? (
                <Typography variant='body2' color='textSecondary'>
                  {subTask ? 'No Team' : '-'}
                </Typography>
              ) : (
                rowGroups.map((group, index, array) => (
                  <Fragment key={group.id}>
                    {subTask?.group?.id === group.id ? (
                      <strong>{group.name}</strong>
                    ) : (
                      group.name
                    )}
                    {index < array.length - 1 && ', '}
                  </Fragment>
                ))
              )}
            </div>
          </Tooltip>
        </WrappedTableCell>
        {subTask && (
          <TableCell>
            <Checkbox
              size='small'
              color='primary'
              disabled={locked}
              checked={subTask.isOptional || false}
              onClick={event => event.stopPropagation()}
              onChange={(_, checked) => {
                setSubTaskUsers(subTasks => {
                  const index = subTasks.findIndex(
                    subTask => subTask.user.id === user.id
                  );
                  if (index > -1) {
                    subTasks = Array.from(subTasks);
                    subTasks[index] = {
                      ...subTasks[index],
                      isOptional: checked
                    };
                  }
                  return subTasks;
                });
              }}
            />
          </TableCell>
        )}
      </TableRow>
    );
  };

  // set initial users on open only
  useEffect(() => {
    if (open && initialUsersRef.current && initialUsersRef.current.length > 0) {
      setSubTaskUsers(initialUsersRef.current);
    }
  }, [open]);

  useEffect(() => {
    // only get groups if groupIds array is provided
    if (groupIds && groupIds.length > 0) {
      const ids = groupIds.concat(userFilter?.groupIds || []);
      findGroups({ ids: ids.join(',') });
    }
  }, [groupIds, userFilter, findGroups]);

  // handle searching
  useEffect(() => {
    const params: ApiParams = { filter: search, page, limit };
    if (selectedGroupId) {
      params.groupId = selectedGroupId;
    }
    if (userFilter) {
      params.userFilter = userFilter;
    }
    get(params);
  }, [limit, page, search, selectedGroupId, userFilter, get]);

  useEffect(() => {
    if (updatedTask) {
      onAssignRef.current?.(updatedTask);
    }
  }, [updatedTask]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        <Box mt={1} mb={2}>
          <TextField
            autoFocus
            fullWidth
            type='text'
            name='name'
            variant='outlined'
            autoComplete='off'
            label='Employee Name'
            onChange={event => {
              debounceSearch(() => {
                // multiple set state calls inside async or setTimeout
                // can cause multiple re-renders (calls the fetch request twice)
                // NOTE: taken from https://stackoverflow.com/a/65427240/7013346
                ReactDOM.unstable_batchedUpdates(() => {
                  setPage(0);
                  setSearch(event.target.value);
                });
              }, 300);
            }}
          />
          <Box mt={1} mb={2}>
            <GroupSelector
              value={selectedGroupId}
              filterIds={userFilter?.groupIds}
              textFieldProps={{ size: 'medium' }}
              onChange={(group: Group | null) => setSelectedGroupId(group?.id)}
            />
          </Box>
          {groupIds && groupIds.length > 0 && (
            <Box mt={1}>
              <Alert color={isValidSelectedUsers ? 'success' : 'warning'}>
                Assign {groupIds.length} or more Users from:{' '}
                {groupList?.data?.map((group, index, arr) => (
                  <Fragment key={group.id}>
                    {isRequiredGroupMet(group.id) && (
                      <CheckIcon
                        fontSize='small'
                        style={{
                          marginTop: -4,
                          marginRight: 2,
                          verticalAlign: 'middle'
                        }}
                      />
                    )}
                    <strong>
                      {groupIds.filter(id => id === group.id).length}{' '}
                      {group.name}
                    </strong>
                    {index < arr.length - 1 && ', '}
                  </Fragment>
                ))}
              </Alert>
            </Box>
          )}
        </Box>
        <Card>
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Team</TableCell>
                  {subTaskUsers.length > 0 && <TableCell>Optional</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {subTaskUsers.length > 0 && (
                  <>
                    <TableRow className='row-divider'>
                      <TableCell colSpan={maxColSpan}>
                        <Typography variant='overline'>
                          Selected Users
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {subTaskUsers.map(subTask => {
                      return renderRow(subTask.user, subTask);
                    })}
                    <TableRow className='row-divider'>
                      <TableCell colSpan={maxColSpan}>
                        <Typography variant='overline'>
                          Available Users
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={maxColSpan} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {error && (
                  <TableRow>
                    <TableCell colSpan={maxColSpan} padding='none'>
                      <Alert severity='error'>{error.message}</Alert>
                    </TableCell>
                  </TableRow>
                )}
                {filteredUsers.map(user => renderRow(user))}
              </TableBody>
            </Table>
          </StyledTableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={userList?.total || 0}
            rowsPerPage={limit}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
        </Card>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant='contained'
          color='primary'
          disabled={
            isUpdating ||
            !isValidSelectedUsers ||
            subTaskUsersState.length === 0
          }
          endIcon={isUpdating && <CircularProgress size={20} />}
          onClick={() => {
            // use subTaskUsersState and skip lockedUsers
            // also make sure isOptional is properly (no undefined)
            const users = subTaskUsersState.map((subTask): SubTaskUser => {
              return {
                id: subTask.user.id,
                groupId: subTask.group?.id || null,
                isOptional: !!subTask.isOptional
              };
            });
            // only use handleAssign() for multiple selects
            if (multiple && handleAssign) {
              handleAssign(users);
            } else {
              // assume user has value (already checked in disabled prop)
              const user = users[0];
              updateTask({
                userId: user.id,
                groupId: user.groupId,
                isOptional: user.isOptional,
                statePath: parentTask?.status
              });
            }
          }}
        >
          Assign
        </Button>
      </DialogActions>

      <Popover
        open={!!menuSelection}
        id='task-assign-dialog-menu'
        anchorEl={menuSelection?.anchorEl}
        anchorPosition={menuSelection?.anchorPosition}
        anchorReference={
          menuSelection?.anchorPosition ? 'anchorPosition' : undefined
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setMenuSelection(undefined)}
      >
        <StyledForm
          onSubmit={event => {
            event.preventDefault();
            if (!menuSelection) {
              return;
            }
            setSubTaskUsers(subTasks => {
              const index = subTasks.findIndex(
                subTask => subTask.user.id === menuSelection.user.id
              );
              const item: SubTaskUserItem = {
                ...subTasks[index],
                user: menuSelection.user,
                group: menuSelection.group
              };
              if (index === -1) {
                return multiple ? subTasks.concat(item) : [item];
              }
              const copy = Array.from(subTasks);
              copy[index] = item;
              return copy;
            });
            setMenuSelection(undefined);
          }}
        >
          <Box mb={1}>
            <Typography variant='caption'>
              Select the Team to sign off as
            </Typography>
          </Box>
          <Box mb={1}>
            <Autocomplete
              size='small'
              value={menuSelection?.group || null}
              options={menuSelection?.user.groups || []}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                setMenuSelection(selection => {
                  return (
                    selection && { ...selection, group: value || undefined }
                  );
                });
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  autoFocus
                  variant='outlined'
                  placeholder='Team'
                />
              )}
            />
          </Box>
          {menuSelection && (
            <Box mb={1}>
              <Alert severity={menuSelection.group ? 'success' : 'info'}>
                <Typography variant='caption'>
                  User{' '}
                  <strong>
                    {menuSelection.user.firstName} {menuSelection.user.lastName}
                  </strong>{' '}
                  will sign off{' '}
                  {menuSelection.group ? (
                    <>
                      representing the team{' '}
                      <strong>{menuSelection.group.name}</strong>
                    </>
                  ) : (
                    'with no team assignment'
                  )}
                  .
                </Typography>
              </Alert>
            </Box>
          )}
          <Box className='menu-selection-actions'>
            {menuSelection?.selected && (
              <>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => {
                    setSubTaskUsers(subTasks => {
                      const index = subTasks.findIndex(
                        subTask => subTask.user.id === menuSelection.user.id
                      );
                      if (index > -1) {
                        subTasks = Array.from(subTasks);
                        subTasks.splice(index, 1);
                      }
                      return subTasks;
                    });
                    setMenuSelection(undefined);
                  }}
                >
                  Remove
                </Button>
                <div style={{ flex: 1 }} />
              </>
            )}
            <Button
              size='small'
              variant='outlined'
              onClick={() => setMenuSelection(undefined)}
            >
              Cancel
            </Button>
            <Button
              size='small'
              type='submit'
              color='primary'
              variant='outlined'
            >
              {menuSelection?.selected ? 'Update' : 'Assign User'}
            </Button>
          </Box>
        </StyledForm>
      </Popover>
    </Dialog>
  );
};

export default TaskAssignDialog;
